import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, Clock, ArrowsClockwise,
  Factory, Gauge, CurrencyDollar, Timer, ChartLineUp, Hammer, Wrench, Package
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

const CATEGORY_CONFIG = {
  mfg_employment: { icon: Factory, color: "#3b82f6", label: "Manufacturing Employment", accent: "blue" },
  mfg_weekly_hours: { icon: Timer, color: "#10b981", label: "Avg Weekly Hours", accent: "emerald" },
  mfg_hourly_earnings: { icon: CurrencyDollar, color: "#8b5cf6", label: "Avg Hourly Earnings", accent: "violet" },
  mfg_overtime: { icon: Timer, color: "#f59e0b", label: "Avg Overtime Hours", accent: "amber" },
  ppi_all: { icon: Gauge, color: "#ef4444", label: "PPI: Finished Goods", accent: "red" },
  ppi_intermediate: { icon: Package, color: "#f97316", label: "PPI: Intermediate Materials", accent: "orange" },
  industrial_emp: { icon: Hammer, color: "#06b6d4", label: "Durable Goods Employment", accent: "cyan" },
  nondurable_emp: { icon: Wrench, color: "#14b8a6", label: "Nondurable Goods Employment", accent: "teal" },
};

function fmtVal(val, key) {
  if (val === null || val === undefined) return "--";
  if (key.includes("employment") || key.includes("emp")) return `${(val / 1000).toFixed(2)}M`;
  if (key.includes("earnings")) return `$${val.toFixed(2)}`;
  if (key.includes("hours") || key.includes("overtime")) return `${val.toFixed(1)} hrs`;
  if (key.includes("ppi")) return val.toFixed(1);
  return val.toFixed(1);
}

function ChangeIndicator({ value, suffix = "%" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-emerald-400" : "text-red-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

function HeroCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5 space-y-4" data-testid={`pmi-hero-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15` }}>
            <Icon size={16} weight="bold" style={{ color: cfg.color }} />
          </div>
          <div>
            <h3 className="text-sm font-heading font-bold text-zinc-100">{cfg.label}</h3>
            <p className="text-[10px] text-zinc-500">{data.latest_month} {data.latest_year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-data font-bold text-zinc-50">{fmtVal(data.latest_value, category)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Month-over-Month</p>
          <ChangeIndicator value={data.mom_change} />
        </div>
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Year-over-Year</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
      </div>

      <div>
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">24-Month Trend</p>
        <CpiChart data={data.history} color={cfg.color} height={130} />
      </div>
    </div>
  );
}

function ComponentCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4 space-y-3 hover:border-zinc-700 transition-colors" data-testid={`pmi-component-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="bold" style={{ color: cfg.color }} />
          <span className="text-xs font-medium text-zinc-300">{cfg.label}</span>
        </div>
        <span className="text-sm font-data font-bold text-zinc-100">{fmtVal(data.latest_value, category)}</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-zinc-600 mb-0.5">MoM</p>
          <ChangeIndicator value={data.mom_change} />
        </div>
        <div className="text-right">
          <p className="text-[9px] text-zinc-600 mb-0.5">YoY</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
      </div>
      <CpiChart data={data.history} color={cfg.color} height={80} />
      <p className="text-[9px] text-zinc-600">{data.latest_month} {data.latest_year}</p>
    </div>
  );
}

export default function PmiReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("overview");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/pmi`);
      setReport(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load manufacturing data");
    } finally {
      setLoading(false);
    }
  };

  const cats = report?.categories || {};
  const heroKeys = ["mfg_employment", "mfg_weekly_hours", "mfg_hourly_earnings"];
  const componentKeys = ["mfg_overtime", "ppi_all", "ppi_intermediate", "industrial_emp", "nondurable_emp"];
  const allAvailable = Object.entries(cats).filter(([k]) => CATEGORY_CONFIG[k]);

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="pmi-report">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Manufacturing & PMI</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-blue-900/30 text-blue-400 h-4">BLS DATA</Badge>
          </div>
          <p className="text-xs text-zinc-500 flex items-center gap-1.5">
            <Factory size={12} />
            Manufacturing output, employment, earnings & producer prices from BLS
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-7 bg-zinc-800/50 p-0.5">
              <TabsTrigger value="overview" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="pmi-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="components" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="pmi-tab-components">All Indicators</TabsTrigger>
            </TabsList>
          </Tabs>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm" data-testid="pmi-refresh-btn">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="pmi-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchData} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-[320px] bg-zinc-900/50" />)}</div>
        </div>
      )}

      {!loading && !error && view === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pmi-hero-grid">
            {heroKeys.map(key => cats[key] && <HeroCard key={key} category={key} data={cats[key]} />)}
          </div>

          {cats.mfg_employment && (
            <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-4" data-testid="pmi-summary">
              <div className="flex items-center flex-wrap gap-6">
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Mfg Employment</p>
                  <p className="text-lg font-data font-bold text-blue-400">{(cats.mfg_employment.latest_value / 1000).toFixed(2)}M</p>
                </div>
                {cats.mfg_weekly_hours && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Weekly Hours</p>
                    <p className="text-lg font-data font-bold text-emerald-400">{cats.mfg_weekly_hours.latest_value?.toFixed(1)} hrs</p>
                  </div>
                )}
                {cats.mfg_hourly_earnings && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Hourly Earnings</p>
                    <p className="text-lg font-data font-bold text-violet-400">${cats.mfg_hourly_earnings.latest_value?.toFixed(2)}</p>
                  </div>
                )}
                {cats.ppi_all && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">PPI Index</p>
                    <p className="text-lg font-data font-bold text-red-400">{cats.ppi_all.latest_value?.toFixed(1)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3 font-medium">Manufacturing Indicators</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pmi-components-grid">
              {componentKeys.map(key => cats[key] && <ComponentCard key={key} category={key} data={cats[key]} />)}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && view === "components" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pmi-all-components">
          {allAvailable.map(([key, data]) => <HeroCard key={key} category={key} data={data} />)}
        </div>
      )}

      {report && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50" data-testid="pmi-footer">
          <p className="text-[10px] text-zinc-600">Source: {report.source}</p>
          <div className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock size={10} />
            <span>Updated: {new Date(report.report_date).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
